import React, { FC } from 'react';
import { TemplateInsightGroupBy } from 'api/template-insight';
import { Filter } from 'components/molecules/filters/types';
import { WidgetContainer } from 'components/organisms/projects-report/common';
import { DateRange } from 'components/organisms/projects-report/common/range-picker/types';
import { useTranslate } from 'hooks/use-translation';
import { useTemplateInsightQuery } from 'queries/template-insight';
import { AutoHeightTable } from '../auto-heigh-table';
import { ChartWithZoom } from '../chart-with-zoom';
import { Wrapper } from '../components';
import { DefaultEmptyChart, DefaultEmptyTable } from '../empty-state';
import {
  useLineChartForPhases,
  usePhaseReports,
  usePhaseTable,
  usePhaseWidget,
} from '../phases/phase';
import { useWidgetTransformer } from '../utils';
import { TemplateInsightWidget } from './widgets';

export const ByPhases: FC<{ range: DateRange; filter: Filter }> = ({
  range,
  filter,
}) => {
  const { data: templateInsight, isLoading } = useTemplateInsightQuery({
    range,
    groupBy: TemplateInsightGroupBy.Phases,
    filter,
  });
  const phaseReport = usePhaseReports({ templateInsight });
  const { tableColumn, tableData } = usePhaseTable({
    phaseReport,
    isTemplateColumnRequired: false,
  });
  const translate = useTranslate();
  const widgetData = usePhaseWidget({ isLoading, phaseReport });
  const widgetTransformer = useWidgetTransformer();
  const chartOptions = useLineChartForPhases({ templateInsight });

  return (
    <Wrapper>
      <WidgetContainer xs={24} md={8} lg={4} xl={4} xxl={4}>
        <TemplateInsightWidget
          value={widgetData.percentageStartedOnTime}
          title={translate('reports.template_insight.widget.phases_on_time')}
          noDataMsg="No Data"
          headerTransformer={widgetTransformer.toPercent}
        />
        <TemplateInsightWidget
          value={widgetData.averageStartingDelay}
          title={translate(
            'reports.template_insight.widget.avg_starting_delay.title',
          )}
          noDataMsg="No Data"
          headerTransformer={widgetTransformer.toDeviation}
        />
        <TemplateInsightWidget
          value={widgetData.percentageCompletedOnTime}
          title={translate('reports.template_insight.widget.phases_finished')}
          noDataMsg="No Data"
        />
        <TemplateInsightWidget
          value={widgetData.averageCompletionDelay}
          title={translate(
            'reports.template_insight.widget.avg_completion_delay.title',
          )}
          noDataMsg="No Data"
          headerTransformer={widgetTransformer.toDeviation}
        />
      </WidgetContainer>
      <ChartWithZoom
        chartOptions={chartOptions}
        title={translate(
          'reports.template_insight.by-template.by-phase.chart.title',
        )}
        tooltip={translate(
          'reports.template_insight.by-template.by-phase.chart.tooltip',
        )}
        isLoading={isLoading}
        renderEmpty={() =>
          DefaultEmptyChart({ noDataKey: 'report.chart.empty.template.phases' })
        }
      />
      <AutoHeightTable
        dataSource={tableData}
        columns={tableColumn}
        titleLabel={translate(
          'reports.template_insight.overview.phases.table.title',
        )}
        isLoading={isLoading}
        renderEmpty={() =>
          DefaultEmptyTable({ noDataKey: 'report.table.empty.template.phases' })
        }
      />
    </Wrapper>
  );
};
